import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { Extension } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface ExtensionsState {
  extensions: Extension[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ExtensionsState = {
  extensions: [],
  isLoading: false,
  error: null,
};

export const fetchExtensions = createAsyncThunk(
  'extensions/fetchExtensions',
  async (status: string | undefined, { rejectWithValue }) => {
    try {
      const params = status ? `?status=${status}` : '';
      const response = await api.get(`${endpoints.extensions.list}${params}`);
      return (response.data.data || response.data) as Extension[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch extensions'));
    }
  }
);

export const toggleExtension = createAsyncThunk(
  'extensions/toggleExtension',
  async (
    { extensionId, enabled }: { extensionId: string; enabled: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(endpoints.extensions.toggle(extensionId), {
        enabled,
      });
      return (response.data.data || response.data) as Extension;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to toggle extension'));
    }
  }
);

export const updateExtensionSettings = createAsyncThunk(
  'extensions/updateExtensionSettings',
  async (
    {
      extensionId,
      settings,
    }: { extensionId: string; settings: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(endpoints.extensions.settings(extensionId), {
        settings,
      });
      return (response.data.data || response.data) as Extension;
    } catch (error: unknown) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to update extension settings')
      );
    }
  }
);

const extensionsSlice = createSlice({
  name: 'extensions',
  initialState,
  reducers: {
    clearExtensionError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchExtensions
      .addCase(fetchExtensions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExtensions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.extensions = action.payload;
      })
      .addCase(fetchExtensions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // toggleExtension
      .addCase(toggleExtension.fulfilled, (state, action) => {
        const idx = state.extensions.findIndex(
          (e) => e.extensionId === action.payload.extensionId
        );
        if (idx !== -1) {
          state.extensions[idx] = action.payload;
        }
      })
      .addCase(toggleExtension.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // updateExtensionSettings
      .addCase(updateExtensionSettings.fulfilled, (state, action) => {
        const idx = state.extensions.findIndex(
          (e) => e.extensionId === action.payload.extensionId
        );
        if (idx !== -1) {
          state.extensions[idx] = action.payload;
        }
      })
      .addCase(updateExtensionSettings.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearExtensionError } = extensionsSlice.actions;
export default extensionsSlice.reducer;
